import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Table, Input, Button, Space, Switch, Modal,
  Tag, Select, notification, Card, Tooltip, Form
} from 'antd';
import {
  PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined,
  ReloadOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import categoryService from '../../../services/categoryService';
import MyTable from '../../../components/common/MyTable';

const { Search } = Input;
const { Option } = Select;
const { confirm } = Modal;

const CategoryList = () => {
  const [form] = Form.useForm();
  const [categories, setCategories] = useState([]);
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    type: null,
    status: null
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  useEffect(() => {
    fetchCategories();
    fetchCategoryTypes();
  }, [filters, pagination.current, pagination.pageSize]);

  const fetchCategories = async (overrides = {}) => {
    try {
      setLoading(true);
      const params = {
        page: overrides.page || pagination.current,
        limit: overrides.pageSize || pagination.pageSize,
        search: overrides.search !== undefined ? overrides.search : searchText,
        category_type_id: overrides.type !== undefined ? overrides.type : filters.type,
        status: overrides.status !== undefined ? overrides.status : filters.status
      };
      
      const response = await categoryService.getAllCategories(params);
      const categoryPayload = response?.data || {};
      setCategories(categoryPayload.data || []);
      setPagination(prev => ({
        ...prev,
        current: categoryPayload.current_page || params.page || prev.current,
        pageSize: Number(categoryPayload.per_page || params.limit || prev.pageSize),
        total: categoryPayload.total || 0
      }));
    } catch (error) {
      notification.error({
        message: 'Error fetching categories',
        description: error.message || 'Failed to load categories'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryTypes = async () => {
    try {
      const response = await categoryService.getCategoryTypes();
      setCategoryTypes(response?.data || []);
    } catch (error) {
      notification.error({
        message: 'Error fetching category types',
        description: error.message || 'Failed to load category types'
      });
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
    fetchCategories({ search: value, page: 1 });
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, current: 1 });
  };

  const handleResetFilters = () => {
    setFilters({ type: null, status: null });
    setSearchText('');
    setPagination({ ...pagination, current: 1 });
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    form.resetFields();
    form.setFieldsValue({
      category_type_id: categoryTypes[0]?.id,
      status: true
    });
    setModalVisible(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    form.setFieldsValue({
      category_title: category.category_title,
      category_type_id: category.category_type_id,
      status: category.status === 1 || category.status === '1'
    });
    setModalVisible(true);
  };

  const handleSaveCategory = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        category_title: values.category_title,
        category_type_id: values.category_type_id,
        status: values.status ? 1 : 0
      };

      if (editingCategory) {
        await categoryService.updateCategory(editingCategory.id, payload);
        notification.success({ message: 'Category updated successfully' });
      } else {
        await categoryService.createCategory(payload);
        notification.success({ message: 'Category created successfully' });
      }

      setModalVisible(false);
      setEditingCategory(null);
      form.resetFields();
      fetchCategories();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }

      notification.error({
        message: editingCategory ? 'Error updating category' : 'Error creating category',
        description: error.message || 'Please check category details and try again'
      });
    } finally {
      setSaving(false);
    }
  };

  const showDeleteConfirm = (categoryId) => {
    confirm({
      title: 'Are you sure you want to delete this category?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone and may affect products assigned to this category.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(categoryId);
      }
    });
  };

  const handleDelete = async (categoryId) => {
    try {
      await categoryService.deleteCategory(categoryId);
      notification.success({
        message: 'Category deleted successfully'
      });
      fetchCategories();
    } catch (error) {
      notification.error({
        message: 'Error deleting category',
        description: error.message || 'Failed to delete category'
      });
    }
  };

  const handleStatusChange = async (checked, categoryId) => {
    try {
      await categoryService.updateCategoryStatus(categoryId, checked ? 1 : 0);
      notification.success({
        message: `Category ${checked ? 'activated' : 'deactivated'} successfully`
      });
      
      // Update the category status in the current list without refetching
      setCategories(prevCategories => 
        prevCategories.map(category => 
          category.id === categoryId 
            ? { ...category, status: checked ? '1' : '0' } 
            : category
        )
      );
    } catch (error) {
      notification.error({
        message: 'Error updating category status',
        description: error.message || 'Failed to update category status'
      });
    }
  };

  const getCategoryTypeName = (typeId) => {
    const type = categoryTypes.find(t => t.id === typeId);
    return type ? type.category_type : 'Unknown';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
      width: 80
    },
    {
      title: 'Category',
      dataIndex: 'category_title',
      key: 'category_title',
      sorter: (a, b) => a.category_title.localeCompare(b.category_title),
      render: (text) => <span className="font-medium">{text}</span>
    },
    {
      title: 'Type',
      dataIndex: 'category_type_id',
      key: 'category_type_id',
      render: (typeId) => typeId ? (
        <Tag color="blue">{getCategoryTypeName(typeId)}</Tag>
      ) : (
        <Tag color="default">No Type</Tag>
      ),
      filters: categoryTypes.map(type => ({ text: type.category_type, value: type.id })),
      filtered: filters.type !== null,
      filterMultiple: false
    },
    {
      title: 'Product Count',
      key: 'product_count',
      render: (_, record) => (
        <Tag color="green">{record.product_count || '0'}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Switch 
          checked={status === 1 || status === '1'} 
          onChange={(checked) => handleStatusChange(checked, record.id)} 
        />
      ),
      filters: [
        { text: 'Active', value: '1' },
        { text: 'Inactive', value: '0' }
      ],
      filtered: filters.status !== null,
      filterMultiple: false,
      width: 100
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Products">
            <Link to="/admin/all-products" state={{ categoryFilter: record.id }} className="text-blue-500 hover:text-blue-700">
              <EyeOutlined />
            </Link>
          </Tooltip>
          <Tooltip title="Edit Category">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
              className="text-yellow-500 hover:text-yellow-700 p-0"
            />
          </Tooltip>
          <Tooltip title="Delete Category">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => showDeleteConfirm(record.id)}
              className="text-red-500 hover:text-red-700 p-0"
            />
          </Tooltip>
        </Space>
      ),
      width: 120
    }
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Categories Management</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
          className="bg-golden-navy"
        >
          Add Category
        </Button>
      </div>

      <Card className="shadow-md mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex flex-wrap items-center gap-4">
            <Search
              placeholder="Search categories..."
              allowClear
              onSearch={handleSearch}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />
            
            <Select 
              placeholder="Filter by type" 
              style={{ width: 200 }}
              allowClear
              value={filters.type}
              onChange={(value) => handleFilterChange('type', value)}
            >
              {categoryTypes.map(type => (
                <Option key={type.id} value={type.id}>{type.category_type}</Option>
              ))}
            </Select>

            <Select 
              placeholder="Filter by status" 
              style={{ width: 150 }}
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="1">Active</Option>
              <Option value="0">Inactive</Option>
            </Select>

            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleResetFilters}
              className="flex items-center"
            >
              Reset Filters
            </Button>
          </div>

          <Button
            type="primary"
            onClick={fetchCategories}
            icon={<ReloadOutlined />}
            className="bg-blue-500"
          >
            Refresh
          </Button>
        </div>

        <MyTable 
          columns={columns} 
          dataSource={categories} 
          rowKey="id"
          pagination={pagination}
          loading={loading}
          handleTableChange={handleTableChange}
          size="middle"
        />
      </Card>

      <Modal
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingCategory(null);
          form.resetFields();
        }}
        onOk={handleSaveCategory}
        confirmLoading={saving}
        okText={editingCategory ? 'Update Category' : 'Create Category'}
        okButtonProps={{ className: 'bg-golden-navy' }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="category_title"
            label="Category Name"
            rules={[{ required: true, message: 'Please enter a category name' }]}
          >
            <Input placeholder="Category name" />
          </Form.Item>

          <Form.Item
            name="category_type_id"
            label="Category Type"
            rules={[{ required: true, message: 'Please select a category type' }]}
          >
            <Select placeholder="Select type">
              {categoryTypes.map(type => (
                <Option key={type.id} value={type.id}>{type.category_type}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="status" label="Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryList;
